import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';
import { getApplicationById } from '../../services/applications/applicationsService';
import { ApplicationDto } from '../../services/applications/types';

const ApplicationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const [application, setApplication] = useState<ApplicationDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const fetchApplication = useCallback(async () => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to view application details.');
      return;
    }
    
    if (!id) {
      setError('Application ID is missing.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getApplicationById(auth.user || null, id);
      setApplication(data);
    } catch (err) {
      setError('Failed to fetch application details. Please try again.');
      console.error('Error fetching application:', err);
    } finally {
      setIsLoading(false);
    }
  }, [auth.isAuthenticated, auth.user, id]);
  
  useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);
  
  const handleBack = () => {
    navigate('/applications');
  };
  
  
  return (
    <div>
      <PageHeader
        title={isLoading ? 'Loading...' : error ? 'Error' : !application ? 'Not Found' : application.name || 'Application Details'}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Applications", to: "/applications" },
          { label: isLoading ? 'Loading...' : error ? 'Error' : !application ? 'Not Found' : application.name || 'Details' }
        ]}
      />

      {!error && !isLoading && application && (
        <div className="mb-6">
          <Link to={`/applications/${id}/edit`}>
            <Button variant="primary">
              <i className="fas fa-edit mr-2"></i>
              Edit Application
            </Button>
          </Link>
        </div>
      )}
      
      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={fetchApplication}
            >
              Retry
            </Button>
          </div>
        ) : !application ? (
          <div className="bg-yellow-50 text-yellow-700 p-4 rounded-md mb-4">
            Application not found
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={handleBack}
            >
              Back to Applications
            </Button>
          </div>
        ) : (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">General Information</h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Name</span>
                    <span className="mt-1 block">{application.name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Identifier</span>
                    <span className="mt-1 block">{application.identifier || '-'}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Status</span>
                    <span className="mt-1 block">
                      {application.status ? (
                        <StatusBadge status={application.status} />
                      ) : '-'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Integration Details</h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Vendor</span>
                    <span className="mt-1 block">{application.vendor || '-'}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Created At</span>
                    <span className="mt-1 block">
                      {application.created_at ? (
                        new Date(application.created_at).toLocaleString()
                      ) : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Last Updated</span>
                    <span className="mt-1 block">
                      {application.updated_at ? (
                        new Date(application.updated_at).toLocaleString()
                      ) : '-'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
                <div className="space-y-3">
                  <Link to={`/applications/${id}/parameters`} className="block w-full">
                    <Button variant="secondary" fullWidth>
                      <i className="fas fa-cogs mr-2"></i>
                      Manage Parameters
                    </Button>
                  </Link>
                  <Link to={`/applications/${id}/prompts`} className="block w-full">
                    <Button variant="secondary" fullWidth>
                      <i className="fas fa-comment-alt mr-2"></i>
                      View System Prompts
                    </Button>
                  </Link>
                  <Link to={`/applications/${id}/embeddings`} className="block w-full">
                    <Button variant="secondary" fullWidth>
                      <i className="fas fa-vector-square mr-2"></i>
                      View Embeddings
                    </Button>
                  </Link>
                  <Link to={`/applications/${id}/users`} className="block w-full">
                    <Button variant="secondary" fullWidth>
                      <i className="fas fa-users mr-2"></i>
                      View Application Users
                    </Button>
                  </Link>
                  <Link to={`/applications/${id}/tools`} className="block w-full">
                    <Button variant="secondary" fullWidth>
                      <i className="fas fa-code mr-2"></i>
                      Manage Functions
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ApplicationDetails;